import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  Check,
  Home,
  ShieldCheck,
  Users,
  Star,
  Leaf,
  Refrigerator,
  WashingMachine,
  Sofa,
  BedDouble,
  Archive,
  Lamp,
  Fan,
  Wifi,
  ShoppingBag,
  Smartphone,
  Truck,
} from 'lucide-react';

/**
 * VEETU SAAMA — Brand Board v3
 * Palette:  VS Orange #F58220, VS Charcoal #4D4D4D, VS Grey #8E8E8E,
 *           VS Sand #E9D6C1, VS Off White #F7F4EA
 * Fonts:    Headlines — Archivo Black (Monument-style),
 *           Body      — Poppins
 */

const C = {
  orange: '#F58220',
  charcoal: '#4D4D4D',
  grey: '#8E8E8E',
  sand: '#E9D6C1',
  offWhite: '#F7F4EA',
  ink: '#1a1410',
  softLine: '#e6ddcc',
};

const displayFont = { fontFamily: "'Archivo Black', 'Anton', 'Impact', sans-serif" };
const bodyFont = { fontFamily: "'Poppins', 'Inter', system-ui, sans-serif" };
const script = { fontFamily: "'Caveat', cursive" };

/* ---------------- Mini Logo ---------------- */
const BrandMark = ({ size = 56 }) => (
  <svg width={size} height={size} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    {/* House body */}
    <path
      d="M10 30 L32 12 L54 30 L54 54 C54 55.1 53.1 56 52 56 L12 56 C10.9 56 10 55.1 10 54 Z"
      fill={C.charcoal}
    />
    {/* Roof accent orange */}
    <path d="M8 32 L32 12 L56 32" stroke={C.orange} strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    {/* Window */}
    <rect x="18" y="34" width="8" height="8" fill={C.orange} />
    {/* Chimney stroke */}
    <path d="M44 20 L44 14 L48 14 L48 20" stroke={C.charcoal} strokeWidth="3" fill="none" strokeLinecap="round" />
  </svg>
);

const Wordmark = ({ size = 60, showTagline = true }) => (
  <div className="flex flex-col leading-none">
    <div className="flex items-baseline gap-2">
      <span className="uppercase" style={{ ...displayFont, color: C.ink, fontSize: size, letterSpacing: '0.02em', lineHeight: 1 }}>
        Veetu
      </span>
      <span className="uppercase" style={{ ...displayFont, color: C.orange, fontSize: size, letterSpacing: '0.02em', lineHeight: 1 }}>
        Saama
      </span>
    </div>
    {showTagline && (
      <span
        className="uppercase mt-2"
        style={{
          color: C.charcoal,
          fontSize: Math.max(9, size * 0.16),
          letterSpacing: '0.42em',
          fontWeight: 500,
          fontFamily: "'Poppins', sans-serif",
        }}
      >
        Everything &nbsp;For&nbsp; a &nbsp;Better&nbsp; Home
      </span>
    )}
  </div>
);

const FullLogo = ({ size = 60, showTagline = true }) => (
  <div className="flex items-center gap-4">
    <BrandMark size={size * 1.05} />
    <Wordmark size={size} showTagline={showTagline} />
  </div>
);

/* --------------- Reusable UI --------------- */
const Panel = ({ children, className = '', style = {} }) => (
  <div
    className={`rounded-2xl border p-6 md:p-8 ${className}`}
    style={{ background: C.offWhite, borderColor: C.softLine, ...style }}
  >
    {children}
  </div>
);

const SectionLabel = ({ children }) => (
  <div className="flex items-center gap-3 mb-5">
    <div className="w-6 h-[3px] rounded-full" style={{ background: C.orange }} />
    <div className="uppercase text-[11px] tracking-[0.3em] font-semibold" style={{ color: C.charcoal }}>
      {children}
    </div>
  </div>
);

const ValueIcon = ({ Icon, label }) => (
  <div className="flex flex-col items-center text-center gap-3">
    <div
      className="w-14 h-14 rounded-2xl flex items-center justify-center border-2"
      style={{ borderColor: C.orange, background: '#fff8f0' }}
    >
      <Icon className="w-6 h-6" strokeWidth={1.7} style={{ color: C.orange }} />
    </div>
    <div className="uppercase text-[11px] tracking-[0.14em] font-semibold leading-[1.35] max-w-[100px]" style={{ color: C.charcoal }}>
      {label}
    </div>
  </div>
);

const Swatch = ({ hex, name, adjectives, textOnLight = true }) => (
  <div className="flex flex-col">
    <div
      className="rounded-2xl h-24 shadow-sm"
      style={{ background: hex, border: hex.toUpperCase() === '#F7F4EA' ? `1px solid ${C.softLine}` : 'none' }}
    />
    <div className="mt-3">
      <div className="text-[13px] font-bold" style={{ color: C.ink }}>
        {name}
      </div>
      <div className="text-[11px] mt-0.5" style={{ color: C.grey }}>
        {hex}
      </div>
      <div className="text-[11px] mt-2 leading-[1.5]" style={{ color: C.charcoal }}>
        {adjectives}
      </div>
    </div>
  </div>
);

const CategoryIcon = ({ Icon, label }) => (
  <div className="flex flex-col items-center text-center gap-2">
    <div
      className="w-16 h-16 rounded-xl flex items-center justify-center border"
      style={{ borderColor: C.softLine, background: '#fff' }}
    >
      <Icon className="w-7 h-7" strokeWidth={1.5} style={{ color: C.orange }} />
    </div>
    <div className="text-[11px] font-medium leading-tight max-w-[90px]" style={{ color: C.charcoal }}>
      {label}
    </div>
  </div>
);

/* ---------------- Page ---------------- */
const BrandPreview = () => {
  return (
    <div className="min-h-screen" style={{ background: C.offWhite, ...bodyFont, color: C.ink }}>
      {/* Top bar */}
      <div
        className="sticky top-0 z-40 backdrop-blur-md border-b"
        style={{ background: `${C.offWhite}f2`, borderColor: C.softLine }}
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] hover:opacity-70 transition-opacity" style={{ color: C.ink }}>
            <ArrowLeft className="w-4 h-4" /> Back to site
          </Link>
          <div className="text-[10px] uppercase tracking-[0.28em]" style={{ color: C.grey }}>
            Brand Board • v3
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10 lg:py-12">
        {/* HERO ROW — Logo + Lifestyle card */}
        <div className="grid lg:grid-cols-[1.35fr_1fr] gap-6">
          {/* Logo panel */}
          <div className="rounded-2xl p-8 md:p-12 border" style={{ background: C.offWhite, borderColor: C.softLine }}>
            <div className="uppercase text-[11px] tracking-[0.32em] font-semibold" style={{ color: C.charcoal }}>
              Brand Board
            </div>
            <div className="mt-8">
              <FullLogo size={72} />
            </div>
          </div>

          {/* Lifestyle card */}
          <div
            className="rounded-2xl overflow-hidden relative border flex flex-col justify-between"
            style={{ background: C.sand, borderColor: C.softLine, minHeight: 320 }}
          >
            <div className="absolute inset-0 opacity-40" style={{
              background: 'radial-gradient(circle at 80% 20%, #ffffff 0%, transparent 55%)'
            }} />
            <div className="relative p-7 flex items-start justify-between gap-4">
              <div className="uppercase text-[10px] tracking-[0.32em] font-medium" style={{ color: C.charcoal }}>
                Veetu Saama&nbsp;&nbsp;|&nbsp;&nbsp;Brand Guidelines&nbsp;&nbsp;|&nbsp;&nbsp;2026
              </div>
            </div>
            <div className="relative px-7 pb-7 grid grid-cols-[1fr_auto] gap-4 items-end">
              <div style={displayFont} className="text-[38px] leading-[0.95] uppercase" >
                <div style={{ color: C.ink }}>Homes</div>
                <div style={{ color: C.ink }}>Happier</div>
                <div style={{ color: C.orange }}>Everyday</div>
              </div>
              <div className="text-right uppercase text-[11px] tracking-[0.22em] leading-[2]" style={{ color: C.charcoal }}>
                Appliances<br />Kitchens<br />Living<br />Bedrooms<br />Utilities<br />And More
              </div>
            </div>
            <div className="relative flex justify-end pr-6 pb-4">
              <span style={{ ...script, color: C.orange, fontSize: 22 }}>For a Better Tomorrow</span>
            </div>
          </div>
        </div>

        {/* ROW 2: Brand Essence, Brand Values, App Icon placeholder */}
        <div className="mt-6 grid lg:grid-cols-3 gap-6">
          {/* Brand Essence */}
          <Panel>
            <SectionLabel>Brand Essence</SectionLabel>
            <div style={displayFont} className="text-[32px] leading-[1.05]">
              <div style={{ color: C.ink }}>Modern Living.</div>
              <div style={{ color: C.orange }}>Made Simple.</div>
            </div>
            <p className="mt-5 text-[13px] leading-[1.75]" style={{ color: C.charcoal }}>
              Veetu Saama is a modern home & appliances retail brand, making quality
              products for every home accessible, trusted and enjoyable. From
              kitchens to living, we bring the best brands, great service and a
              better everyday for families.
            </p>
          </Panel>

          {/* Brand Values */}
          <Panel>
            <SectionLabel>Brand Values</SectionLabel>
            <div className="grid grid-cols-5 gap-3 mt-2">
              <ValueIcon Icon={Home} label="For Every Home" />
              <ValueIcon Icon={ShieldCheck} label="Trust & Quality" />
              <ValueIcon Icon={Users} label="People First" />
              <ValueIcon Icon={Star} label="Modern & Relevant" />
              <ValueIcon Icon={Leaf} label="Better Tomorrow" />
            </div>
            <div className="mt-8 pt-6 border-t text-center uppercase tracking-[0.32em] text-[11px]" style={{ borderColor: C.softLine, color: C.grey }}>
              A Complete Home Retail Destination
            </div>
          </Panel>

          {/* App Icon (placeholder) */}
          <Panel>
            <SectionLabel>App Icon</SectionLabel>
            <div className="flex items-center gap-6">
              <div
                className="w-28 h-28 rounded-[26px] flex items-center justify-center border-2 border-dashed relative"
                style={{ borderColor: C.orange, background: '#fff8f0' }}
              >
                <div className="text-center px-3">
                  <div className="uppercase text-[9px] tracking-[0.24em] font-semibold" style={{ color: C.orange }}>
                    Awaiting
                  </div>
                  <div className="uppercase text-[9px] tracking-[0.24em] font-semibold mt-0.5" style={{ color: C.charcoal }}>
                    Brand Icon
                  </div>
                </div>
              </div>
              <div>
                <div style={displayFont} className="uppercase text-[22px] leading-[1.05]">
                  <span style={{ color: C.ink }}>Simple. </span>
                  <span style={{ color: C.ink }}>Recognisable. </span>
                  <span style={{ color: C.orange }}>Always Home.</span>
                </div>
                <div className="text-[12px] mt-3 leading-[1.6]" style={{ color: C.charcoal }}>
                  Final app tile will be supplied by the brand team and dropped into
                  <code className="text-[11px]" style={{ color: C.orange }}> /public</code>.
                </div>
              </div>
            </div>
          </Panel>
        </div>

        {/* ROW 3: Colour Palette (wide) + Typography + Icon Style */}
        <div className="mt-6 grid lg:grid-cols-[1.35fr_1fr_1.15fr] gap-6">
          {/* Colour Palette */}
          <Panel>
            <SectionLabel>Colour Palette</SectionLabel>
            <div className="grid grid-cols-5 gap-4">
              <Swatch hex="#F58220" name="VS Orange" adjectives={<>Energy<br />Warmth<br />Modern</>} />
              <Swatch hex="#4D4D4D" name="VS Charcoal" adjectives={<>Trust<br />Strength<br />Premium</>} />
              <Swatch hex="#8E8E8E" name="VS Grey" adjectives={<>Balance<br />Neutral<br />Modern</>} />
              <Swatch hex="#E9D6C1" name="VS Sand" adjectives={<>Warm<br />Friendly<br />Approachable</>} />
              <Swatch hex="#F7F4EA" name="VS Off White" adjectives={<>Clean<br />Calm<br />Spacious</>} />
            </div>
          </Panel>

          {/* Typography */}
          <Panel>
            <SectionLabel>Typography</SectionLabel>
            <div>
              <div className="text-[12px] mb-1" style={{ color: C.charcoal }}>
                Primary Font <span style={{ color: C.grey }}>(Logo Style / Headlines)</span>
              </div>
              <div style={displayFont} className="uppercase text-[34px] leading-none" >
                Monument
              </div>
              <div className="text-[11px] mt-2 leading-[1.5] tracking-wider" style={{ color: C.charcoal }}>
                A B C D E F G H I J K L M N O P Q R S T U V W X Y Z<br />
                0 1 2 3 4 5 6 7 8 9
              </div>
            </div>

            <div className="mt-6 pt-5 border-t" style={{ borderColor: C.softLine }}>
              <div className="text-[12px] mb-1" style={{ color: C.charcoal }}>
                Secondary Font <span style={{ color: C.grey }}>(Body / UI / Print)</span>
              </div>
              <div className="text-[30px] font-medium leading-none" style={{ fontFamily: "'Poppins', sans-serif" }}>
                Poppins
              </div>
              <div className="text-[11px] mt-2 leading-[1.5] tracking-wider" style={{ color: C.charcoal }}>
                A B C D E F G H I J K L M N O P Q R S T U V W X Y Z<br />
                a b c d e f g h i j k l m n o p q r s t u v w x y z<br />
                0 1 2 3 4 5 6 7 8 9
              </div>
            </div>
          </Panel>

          {/* Icon Style */}
          <Panel>
            <SectionLabel>Icon Style</SectionLabel>
            <div className="grid grid-cols-3 gap-5">
              <CategoryIcon Icon={WashingMachine} label="Kitchen Appliances" />
              <CategoryIcon Icon={Refrigerator} label="Home Appliances" />
              <CategoryIcon Icon={Sofa} label="Living Essentials" />
              <CategoryIcon Icon={BedDouble} label="Bedroom Essentials" />
              <CategoryIcon Icon={Archive} label="Storage & Organisers" />
              <CategoryIcon Icon={Lamp} label="Lighting" />
              <CategoryIcon Icon={Fan} label="Fans & Cooling" />
              <CategoryIcon Icon={Wifi} label="Smart Home" />
            </div>
          </Panel>
        </div>

        {/* ROW 4: Visual Style + Store Front + Brand in Use */}
        <div className="mt-6 grid lg:grid-cols-[1fr_1.35fr_0.9fr] gap-6">
          {/* Visual Style */}
          <Panel>
            <SectionLabel>Visual Style</SectionLabel>
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: 'Modern Kitchens', img: 'https://images.unsplash.com/photo-1556909212-d5b604d0c90d?w=400&q=70' },
                { label: 'Warm Living Spaces', img: 'https://images.unsplash.com/photo-1567016432779-094069958ea5?w=400&q=70' },
                { label: 'Comfortable Bedrooms', img: 'https://images.unsplash.com/photo-1505693416388-ac5ce068fe85?w=400&q=70' },
                { label: 'Functional Essentials', img: 'https://images.unsplash.com/photo-1585515320310-259814833e62?w=400&q=70' },
              ].map((s) => (
                <div key={s.label} className="rounded-lg overflow-hidden">
                  <div className="aspect-[4/3] overflow-hidden">
                    <img src={s.img} alt={s.label} className="w-full h-full object-cover" />
                  </div>
                  <div className="uppercase text-[10px] tracking-[0.18em] font-semibold mt-2" style={{ color: C.charcoal }}>
                    {s.label}
                  </div>
                </div>
              ))}
            </div>
          </Panel>

          {/* Store Front visualization */}
          <Panel style={{ background: '#fff' }}>
            <SectionLabel>Store Front <span className="normal-case font-normal" style={{ color: C.grey }}>(Visualisation)</span></SectionLabel>
            <div className="relative rounded-xl overflow-hidden" style={{ background: C.charcoal, aspectRatio: '16/8.2' }}>
              {/* Store frame */}
              <div className="absolute inset-0" style={{
                background: `linear-gradient(180deg, ${C.charcoal} 0%, ${C.charcoal} 34%, #f0e9dd 34%, #f0e9dd 100%)`
              }} />
              {/* Sign */}
              <div className="absolute top-[8%] left-0 right-0 flex justify-center">
                <FullLogo size={44} showTagline />
              </div>
              {/* Store windows */}
              <div className="absolute bottom-0 left-[6%] right-[6%] top-[38%] grid grid-cols-3 gap-3 pb-2">
                {[0,1,2].map(i => (
                  <div key={i} className="rounded-t-lg" style={{
                    background: 'linear-gradient(180deg, #fff5e6 0%, #ffe6c6 100%)',
                    boxShadow: 'inset 0 12px 20px rgba(245,130,32,0.08)'
                  }} />
                ))}
              </div>
              {/* Right-side badge */}
              <div className="absolute right-[8%] bottom-[16%] text-right leading-[1.25]" style={{ color: C.ink }}>
                <div style={displayFont} className="uppercase text-[13px]">Better</div>
                <div style={displayFont} className="uppercase text-[13px]">Homes</div>
                <div style={{ ...displayFont, color: C.orange }} className="uppercase text-[13px]">Brighter</div>
                <div style={{ ...displayFont, color: C.orange }} className="uppercase text-[13px]">Lives</div>
              </div>
            </div>
          </Panel>

          {/* Brand in Use */}
          <Panel>
            <SectionLabel>Brand in Use</SectionLabel>
            <div className="grid grid-cols-2 gap-4">
              {/* Shopping bag */}
              <div className="rounded-xl p-4 flex flex-col items-center gap-2" style={{ background: '#fff' }}>
                <div className="w-full aspect-[3/4] rounded-lg flex items-center justify-center relative overflow-hidden" style={{ background: C.sand }}>
                  <ShoppingBag className="w-14 h-14" strokeWidth={1.2} style={{ color: C.charcoal }} />
                  <div className="absolute bottom-3 text-center">
                    <div style={{ ...displayFont, color: C.orange, fontSize: 10 }} className="uppercase">Veetu Saama</div>
                  </div>
                </div>
                <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: C.charcoal }}>Bag</div>
              </div>
              {/* Mobile app */}
              <div className="rounded-xl p-4 flex flex-col items-center gap-2" style={{ background: '#fff' }}>
                <div className="w-full aspect-[3/4] rounded-lg flex items-center justify-center border" style={{ background: C.offWhite, borderColor: C.softLine }}>
                  <Smartphone className="w-14 h-14" strokeWidth={1.2} style={{ color: C.orange }} />
                </div>
                <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: C.charcoal }}>Mobile App</div>
              </div>
              {/* Delivery truck */}
              <div className="rounded-xl p-4 flex flex-col items-center gap-2 col-span-2" style={{ background: '#fff' }}>
                <div className="w-full aspect-[16/6] rounded-lg flex items-center justify-center" style={{ background: '#fff' }}>
                  <Truck className="w-20 h-20" strokeWidth={1.2} style={{ color: C.charcoal }} />
                  <div className="ml-3" style={{ ...displayFont, color: C.orange, fontSize: 20 }} >Veetu Saama</div>
                </div>
                <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: C.charcoal }}>Fleet Livery</div>
              </div>
            </div>
          </Panel>
        </div>

        {/* Footer nav mockup */}
        <div className="mt-8 rounded-2xl overflow-hidden">
          <div className="flex items-center gap-6 px-6 py-4" style={{ background: '#fff', borderTop: `1px solid ${C.softLine}` }}>
            <FullLogo size={26} showTagline={false} />
            <div className="hidden md:flex items-center gap-6 ml-4 uppercase text-[12px] tracking-[0.24em] font-semibold" style={{ color: C.charcoal }}>
              <span>Homes</span>
              <span style={{ color: C.orange }}>|</span>
              <span>Appliances</span>
              <span style={{ color: C.orange }}>|</span>
              <span>Lifestyle</span>
              <span style={{ color: C.orange }}>|</span>
              <span>Together</span>
            </div>
            <div className="ml-auto" style={{ ...script, color: C.orange, fontSize: 22 }}>For a Better Tomorrow</div>
          </div>
        </div>

        {/* Approve CTA */}
        <div
          className="mt-10 rounded-2xl p-10 md:p-14 text-center"
          style={{ background: C.charcoal }}
        >
          <div className="uppercase text-[11px] tracking-[0.32em] font-semibold mb-4" style={{ color: C.orange }}>
            Ready when you are
          </div>
          <h3 className="max-w-3xl mx-auto uppercase" style={{ ...displayFont, color: C.offWhite, fontSize: 42, lineHeight: 1.05 }}>
            Approve to roll this identity across the site.
          </h3>
          <p className="text-[14px] mt-4 max-w-xl mx-auto leading-[1.7]" style={{ color: `${C.offWhite}b3` }}>
            Header, footer, buttons, chat widget and category tiles will inherit
            the new palette, wordmark, tagline and icon style. Final app tile
            will be supplied by the brand team.
          </p>
          <div className="mt-8 flex items-center justify-center gap-3 flex-wrap">
            <div className="flex items-center gap-2 px-5 py-2.5 rounded-full text-[12px] uppercase tracking-[0.2em] font-semibold" style={{ background: C.orange, color: '#fff' }}>
              <Check className="w-4 h-4" /> Reply &ldquo;approve&rdquo; to apply
            </div>
            <div className="flex items-center gap-2 px-5 py-2.5 rounded-full text-[12px] uppercase tracking-[0.2em] font-semibold border" style={{ borderColor: `${C.offWhite}33`, color: C.offWhite }}>
              Suggest tweaks
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BrandPreview;
